import { FC, useMemo } from "react";
import { ChatUserInfo } from "@xapp/stentor-chat-widget";
import { ChatMsgDetail, ToolCallInfo } from "../../store/ChatAction";
import { isAgent } from "../../utils";
import { ChatMessageBubble } from "../ChatMessageBubble";
import { ToolCallDisplay } from "../ToolCallDisplay";
import "./ChatTextMessage.scss";

/**
 * Parses basic markdown to HTML.
 * Supports: bold, italic, links, headers, and unordered lists.
 */
function parseMarkdown(text: string): string {
    if (!text) return "";

    let html = text
        // Escape HTML entities first
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        // Headers (must be at start of line)
        .replace(/^### (.+)$/gm, "<h4>$1</h4>")
        .replace(/^## (.+)$/gm, "<h3>$1</h3>")
        .replace(/^# (.+)$/gm, "<h2>$1</h2>")
        // Bold (** or __)
        .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
        .replace(/__(.+?)__/g, "<strong>$1</strong>")
        // Italic (* or _) - must come after bold
        .replace(/\*([^*]+?)\*/g, "<em>$1</em>")
        .replace(/_([^_]+?)_/g, "<em>$1</em>")
        // Links [text](url)
        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
        // Unordered lists (- or *)
        .replace(/^[-*] (.+)$/gm, "<li>$1</li>")
        // Line breaks
        .replace(/\n/g, "<br>");

    // Wrap consecutive <li> elements in <ul>
    html = html.replace(/(<li>.*?<\/li>)(<br>)?/g, "$1");
    html = html.replace(/(<li>.*?<\/li>)+/g, "<ul>$&</ul>");
    // Clean up <br> inside <ul>
    html = html.replace(/<ul>(.*?)<\/ul>/g, (match) => {
        return match.replace(/<br>/g, "");
    });

    return html;
}

export interface ChatTextMessageProps {
    readonly message: ChatMsgDetail;
    readonly sibling: boolean;
    /** Agent/user info for avatar rendering */
    readonly user?: ChatUserInfo;
    /** Tool calls to display above the message bubble */
    readonly toolCalls?: ToolCallInfo[];
    /** When true, shows expanded debug info for tool calls */
    readonly debugMode?: boolean;
}

export const ChatTextMessage: FC<ChatTextMessageProps> = (props) => {
    const message = props.message;
    const date = new Date(message.timestamp);

    const time = date.getHours() + ":" + date.getMinutes();

    const agentMessage = isAgent(props.message.user?.nick);
    const isStreaming = message.isStreaming;

    // Parse markdown for agent messages
    const parsedHtml = useMemo(() => {
        if (agentMessage && message.msg.text) {
            return parseMarkdown(message.msg.text);
        }
        return null;
    }, [agentMessage, message.msg.text]);

    // No tail for streaming messages (or messages that were streamed)
    const wasStreamed = message.wasStreamed;
    const showTail = agentMessage && !props.sibling && !isStreaming && !wasStreamed;

    const hasToolCalls = props.toolCalls && props.toolCalls.length > 0;

    return (
        <div className={`chat-msg${isStreaming ? " chat-msg--streaming" : ""}`}>
            {/* Tool progress above the bubble */}
            {hasToolCalls && (
                <ToolCallDisplay toolCalls={props.toolCalls} debugMode={props.debugMode} />
            )}
            <div className="chat-text-container">
                <ChatMessageBubble
                    owner={agentMessage ? "others" : "mine"}
                    hasTail={showTail}>
                    <span className="message-sr-only">
                        {"at " + time + (agentMessage ? (isStreaming ? " the bot is typing" : " the bot said") : " the user said")}
                    </span>
                    {parsedHtml ? (
                        <span className="chat-msg__content" dangerouslySetInnerHTML={{ __html: parsedHtml }} />
                    ) : (
                        <span className="chat-msg__content">{message.msg.text}</span>
                    )}
                    {isStreaming && <span className="streaming-cursor" aria-hidden="true" />}
                </ChatMessageBubble>
            </div>
        </div>
    );
};
